import React from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import SingleSelect from "@/components/controls/SingleSelect";
import ServiceDetailRow from "./ServiceDetailRow";
import { getBillingFrequencyLabel, getServiceDurationLabel } from "../consts";
import {
  BillingFrequency,
  ChargeType,
  ServiceDuration,
  ServiceItemDetail
} from "@/pages/settings/pages/products-v2/types";

import { ChevronDown, ChevronUp } from "@dreamhub/designsystem/icons";

interface ServiceTermsSectionProps {
  selectedService: ServiceItemDetail;
  selectedPricingId: string;
  // The terms the line actually runs on. An existing line keeps the ones it
  // recorded; only a new line — or one whose term the user just switched —
  // reads them off the catalog pricing row, which may have moved since.
  serviceDuration: ServiceDuration | null;
  // null where the line records no unit price at all — rendered as an em dash
  // rather than coerced, so the panel never shows a figure the line never had.
  price: number | null;
  billingFrequency: BillingFrequency | null;
  pricingOptions: { id: string; displayName: string; subtitle?: string }[];
  isCustomServiceDuration: boolean;
  isEmptyTermClick: boolean;
  isFlatRatePricing?: boolean;
  onPricingSelect: (id: string) => void;
  onEmptyTermClick: () => void;
}

const ServiceTermsSection: React.FC<ServiceTermsSectionProps> = ({
  selectedService,
  selectedPricingId,
  serviceDuration,
  price,
  billingFrequency,
  pricingOptions,
  isCustomServiceDuration,
  isEmptyTermClick,
  isFlatRatePricing = false,
  onPricingSelect,
  onEmptyTermClick
}) => {
  const { t } = useTranslation();
  // Matches getServiceDurationLabel's own convention for an absent value.
  const priceLabel = price == null ? "—" : `$${price || "0"}`;

  return (
    <div className="bg-surface-elevation2 border-[0.5px] border-border-border1 rounded-xl p-4 flex flex-col gap-4">
      <div className="flex items-start justify-between w-full">
        <Text variant="label">{t("contractTerms.serviceTerms")}</Text>
        {selectedPricingId && selectedService.pricing.length > 1 && (
          <SingleSelect
            fieldName="service-terms-change"
            options={pricingOptions}
            selectedValue={selectedPricingId}
            onSelection={(value) => onPricingSelect(value as string)}
            variant="form"
            popoverClassName="w-[450px] mr-6"
            customTriggerComponent={
              <Button variant="tertiary">{t("contractTerms.change")}</Button>
            }
          />
        )}
      </div>

      {!selectedPricingId ? (
        <SingleSelect
          fieldName="service-terms"
          options={pricingOptions}
          selectedValue={selectedPricingId}
          onSelection={(value) => onPricingSelect(value as string)}
          variant="form"
          className="w-full"
          customEmptyTriggerComponent={
            <Button
              onClick={onEmptyTermClick}
              variant="tertiary"
              className="w-full"
            >
              {t("contractTerms.selectTerm")}{" "}
              {isEmptyTermClick ? <ChevronDown /> : <ChevronUp />}
            </Button>
          }
        />
      ) : isCustomServiceDuration ? (
        <Text variant="body">{t("contractTerms.customTerms")}</Text>
      ) : selectedService.chargeType === ChargeType.ONE_TIME ? (
        <div className="flex flex-col gap-0">
          <ServiceDetailRow
            label={t("contractTerms.pricePerUnit")}
            value={priceLabel}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-0">
          <ServiceDetailRow
            label={t("contractTerms.servicePeriod")}
            value={getServiceDurationLabel(serviceDuration, t)}
          />
          <ServiceDetailRow
            label={t(
              isFlatRatePricing
                ? "contractTerms.priceForEntirePeriod"
                : "contractTerms.pricePerUnitMonth"
            )}
            value={priceLabel}
          />
          <ServiceDetailRow
            label={t("contractTerms.billingFrequency")}
            value={getBillingFrequencyLabel(billingFrequency, t)}
          />
        </div>
      )}
    </div>
  );
};

export default ServiceTermsSection;
